/**
 * "Build a search from the request" (Lake Stevens R2-12): RedactIt reads the
 * case's request and suggests the parts of a Microsoft Purview search; the user
 * edits them, then either has RedactIt run it in Purview and import the export
 * (sc-11767, when Purview is connected) or copies the KeyQL, runs and exports it
 * in Purview, and imports the export from Data collection.
 *
 * Shown inside the new-case wizard's Data collection step (eDiscovery pane) and
 * from a case's menu. The server suggests the parts (internal LLM, rules as
 * fallback) and `lib/purviewQuery` builds the KeyQL, live, on every edit;
 * nothing reaches Purview until the user selects Run in Purview.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle, AlertTriangle, ArrowLeft, Check, Clock, Copy, ExternalLink, Filter, Loader2, Plus, Sparkles, X,
} from 'lucide-react';
import { Button } from '../ui/Button';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/Input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { cn } from '../ui/utils';
import { apiHelpers, intakeApi } from '../../lib/api';
import type { EDiscoverySource } from '../../types';
import { PurviewRunPanel, type PurviewRunCase } from './PurviewRunPanel';
import {
  buildKeyQL, fromEditable, nearExample, NO_STANDARD_EXCLUSIONS, PURVIEW_LIMITS, splitTerms, toEditable,
  type EditableParts, type PurviewQuerySuggestion, type QueryVariant, type StandardExclusions,
} from '../../lib/purviewQuery';

/** Purview's eDiscovery home, when the case's own Searches link isn't known. */
export const PURVIEW_EDISCOVERY_URL = 'https://purview.microsoft.com/ediscovery';

const LOADING_LINES = ['Reading the request…', 'Finding people, dates and project names…', 'Drafting the query…'];

export interface PurviewQueryBuilderProps {
  caseId: string;
  /** The request text to start from (the case's notes); editable here. */
  requestText: string;
  /** "R2-12 · Harbor & Vale LLP · due Nov 18, 2026" */
  requestMeta?: string | null;
  /** The Purview case picked in Data collection, when there is one. */
  purviewCaseName?: string | null;
  /** That case's Searches page in Purview. */
  purviewUrl?: string | null;
  /** That case's id in Purview, for Run in Purview. */
  purviewCaseId?: string | null;
  /** Purview is connected for the tenant: offer Run in Purview (sc-11767). */
  canRunInPurview?: boolean;
  /** Run in Purview started a search; the source shows its progress. */
  onRunStarted?: (source: EDiscoverySource) => void;
  /** 'wizard': back to Data collection to import. 'case': copy only. */
  context: 'wizard' | 'case';
  /** Wizard: back to Data collection. */
  onBack?: () => void;
  /** Called after the query is copied. */
  onCopied?: () => void;
  /** Start with a suggestion already made (tests, reopening). */
  initial?: PurviewQuerySuggestion;
}

type Status = 'idle' | 'loading' | 'done' | 'error';

export function PurviewQueryBuilder({
  caseId, requestText: initialText, requestMeta, purviewCaseName, purviewUrl, purviewCaseId, canRunInPurview,
  onRunStarted, context, onBack, onCopied, initial,
}: PurviewQueryBuilderProps) {
  const [requestText, setRequestText] = useState(initialText);
  const [status, setStatus] = useState<Status>(initial ? 'done' : 'idle');
  const [loadingStep, setLoadingStep] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<PurviewQuerySuggestion['source'] | null>(initial?.source ?? null);
  const [edit, setEdit] = useState<EditableParts | null>(initial ? toEditable(initial.parts) : null);
  const [standard, setStandard] = useState<StandardExclusions>(NO_STANDARD_EXCLUSIONS);
  const [variant, setVariant] = useState<QueryVariant>('focused');
  const [sources, setSources] = useState('');
  const [copied, setCopied] = useState(false);
  const keyqlRef = useRef<HTMLTextAreaElement>(null);
  const seq = useRef(0);

  // The request text arrives after mount when the case loads late.
  const touchedText = useRef(false);
  useEffect(() => {
    if (!touchedText.current) setRequestText(initialText);
  }, [initialText]);

  useEffect(() => {
    if (status !== 'loading') return;
    const timer = setInterval(() => setLoadingStep((s) => Math.min(s + 1, LOADING_LINES.length - 1)), 1500);
    return () => clearInterval(timer);
  }, [status]);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2500);
    return () => clearTimeout(timer);
  }, [copied]);

  const parts = useMemo(() => (edit ? fromEditable(edit) : null), [edit]);
  const built = useMemo(() => (parts ? buildKeyQL(parts, standard, variant) : null), [parts, standard, variant]);
  // "Copied" only describes the query that was copied: any change (an edit,
  // the other variant, an exclusion) clears it.
  const keyqlText = built?.keyql ?? '';
  useEffect(() => {
    setCopied(false);
  }, [keyqlText]);

  const focusedFragments = useMemo(
    () => (parts ? buildKeyQL(parts, standard, 'focused').fragments : null),
    [parts, standard],
  );

  const generate = async () => {
    const mine = ++seq.current;
    setStatus('loading');
    setLoadingStep(0);
    setError(null);
    setCopied(false);
    try {
      const result = await intakeApi.suggestPurviewQuery(caseId, requestText);
      if (mine !== seq.current) return;
      setEdit(toEditable(result.parts));
      setSource(result.source);
      setVariant('focused');
      setStandard(NO_STANDARD_EXCLUSIONS);
      setStatus('done');
    } catch (err) {
      if (mine !== seq.current) return;
      setError(apiHelpers.errorDetail(err) || 'Could not draft a query. Try again in a moment.');
      setStatus(edit ? 'done' : 'error');
    }
  };

  const copy = async () => {
    if (!built?.keyql) return;
    const done = () => {
      setCopied(true);
      onCopied?.();
    };
    try {
      await navigator.clipboard.writeText(built.keyql);
      done();
    } catch {
      // No clipboard API (or denied): select the text and copy the old way.
      const el = keyqlRef.current;
      if (el) {
        el.focus();
        el.select();
        try {
          if (document.execCommand('copy')) done();
        } catch { /* the text stays selected for Ctrl+C */ }
      }
    }
  };

  const update = (patch: Partial<EditableParts>) => setEdit((prev) => (prev ? { ...prev, ...patch } : prev));
  const updateGroup = (index: number, text: string) =>
    setEdit((prev) => prev && { ...prev, groups: prev.groups.map((g, i) => (i === index ? { ...g, text } : g)) });
  const removeGroup = (index: number) =>
    setEdit((prev) => prev && { ...prev, groups: prev.groups.filter((_, i) => i !== index) });
  const addGroup = () =>
    setEdit((prev) => prev && { ...prev, groups: [...prev.groups, { label: '', text: '', reason: '' }] });

  const caseLabel = purviewCaseName ? `the Purview case ${purviewCaseName}` : 'your Purview case';
  const sourceLines = splitTerms(sources.replace(/\n/g, ','));
  const pickedCase: PurviewRunCase | null = purviewCaseId && purviewCaseName
    ? { externalId: purviewCaseId, name: purviewCaseName }
    : null;
  const tagline = canRunInPurview
    ? 'RedactIt drafts the query, then runs it in Purview for you, or you copy it and run it there.'
    : 'RedactIt drafts the query; you run and export it in Purview.';

  return (
    <div className="space-y-5">
      {onBack && (
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="outline" size="sm" onClick={onBack}>
            <ArrowLeft className="h-3.5 w-3.5" /> Data collection
          </Button>
          <div className="min-w-0 flex-1">
            <h2 className="font-title text-lg font-semibold tracking-tight text-gray-900">Purview search query</h2>
            <p className="text-xs text-gray-500">
              For {caseLabel}. {tagline}
            </p>
          </div>
        </div>
      )}
      {!onBack && (
        <p className="text-xs text-gray-500">{tagline}</p>
      )}

      {/* Request text */}
      <div className="space-y-2.5 rounded-lg border border-gray-200 bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Label htmlFor="purview-request-text" className="text-xs">Request text (from the case)</Label>
          {requestMeta && <span className="text-xs text-gray-500">{requestMeta}</span>}
        </div>
        <Textarea id="purview-request-text" rows={4} value={requestText}
          onChange={(e) => { touchedText.current = true; setRequestText(e.target.value); }}
          placeholder="Paste the records request here." />
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={generate} disabled={status === 'loading' || !requestText.trim()}>
            {status === 'loading' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {status === 'loading' ? 'Generating…' : edit ? 'Generate again' : 'Generate Purview query'}
          </Button>
          <span className="text-xs text-gray-500">
            {requestText.trim()
              ? 'Uses the request text and the case’s dates. Nothing is sent to Purview.'
              : 'Add the request text first.'}
          </span>
        </div>
        {error && (
          <p className="flex items-center gap-1 text-xs text-rose-600" role="alert">
            <AlertCircle className="h-3 w-3 shrink-0" /> {error}
          </p>
        )}
      </div>

      {status === 'idle' && <EmptyState />}
      {status === 'error' && !edit && <EmptyState />}
      {status === 'loading' && <LoadingState line={LOADING_LINES[loadingStep]} />}

      {status === 'done' && edit && built && parts && focusedFragments && (
        <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-2">
          {/* Query parts */}
          <div className="min-w-0 space-y-3">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Query parts</p>
            {source === 'rules' && (
              <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800" role="status">
                The AI wasn&apos;t available, so these parts come from simple rules. Check each one.
              </p>
            )}

            {edit.groups.map((group, i) => (
              <PartCard key={i} title={group.label || 'Keywords and phrases'} htmlFor={`purview-kw-${i}`}
                reason={group.reason} fragment={focusedFragments.keywords[i]}
                action={(
                  <button type="button" onClick={() => removeGroup(i)} aria-label={`Remove ${group.label || 'keyword group'}`}
                    className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600">
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}>
                <Input id={`purview-kw-${i}`} value={group.text} onChange={(e) => updateGroup(i, e.target.value)}
                  placeholder="20th Street SE, 20th St SE, CIP 24-07" />
                <p className="text-xs text-gray-500">
                  Comma-separated, matched as one OR group. Multi-word terms become exact phrases.
                </p>
              </PartCard>
            ))}
            <Button variant="outline" size="sm" onClick={addGroup}>
              <Plus className="h-3.5 w-3.5" /> {edit.groups.length ? 'Add another keyword group' : 'Add keywords'}
            </Button>
            {edit.groups.length > 1 && (
              <p className="text-xs text-gray-500">Every keyword group must match: one word from each.</p>
            )}

            <PartCard title="People and documents" reason={edit.peopleReason || edit.documentReason}
              fragment={[focusedFragments.people, focusedFragments.documents && `documents: ${focusedFragments.documents}`]
                .filter(Boolean).join('   ·   ')}>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Field id="purview-side-a" label="Agency side (email or domain)" value={edit.sideA}
                  onChange={(sideA) => update({ sideA })} placeholder="name@agency.gov" />
                <Field id="purview-side-b" label="Other party (email or domain)" value={edit.sideB}
                  onChange={(sideB) => update({ sideB })} placeholder="vendor.com" />
              </div>
              <Field id="purview-doc-terms" label="Names to find in documents" value={edit.documentTerms}
                onChange={(documentTerms) => update({ documentTerms })} placeholder="Northline" />
              <p className="text-xs text-gray-500">
                On email, participants matches From, To, Cc and Bcc, and a domain matches anyone there. Files have
                no participants, so documents must mention the names instead.
              </p>
            </PartCard>

            <PartCard title="Date range" reason={edit.dateReason} fragment={focusedFragments.dates}>
              <div className="flex flex-wrap items-end gap-2">
                <Field id="purview-date-start" label="From" type="date" value={edit.start}
                  onChange={(start) => update({ start })} />
                <span className="pb-2 text-gray-500">–</span>
                <Field id="purview-date-end" label="To" type="date" value={edit.end}
                  onChange={(end) => update({ end })} />
              </div>
              {edit.start && edit.end && edit.start > edit.end && (
                <p className="text-xs text-rose-600" role="alert">The start date is after the end date.</p>
              )}
              <p className="text-xs text-gray-500">
                Email is dated by sent, documents by lastmodifiedtime. Purview compares dates in UTC.
              </p>
            </PartCard>

            <PartCard title="Item types" reason={edit.typesReason} fragment={focusedFragments.types}>
              <div className="flex flex-wrap gap-4">
                <Toggle id="purview-type-email" label="Email" checked={edit.email} onChange={(email) => update({ email })} />
                <Toggle id="purview-type-docs" label="Documents" checked={edit.documents}
                  onChange={(documents) => update({ documents })} />
                <Toggle id="purview-type-meetings" label="Meetings (calendar)" checked={edit.meetings}
                  onChange={(meetings) => update({ meetings })} />
              </div>
              <p className="text-xs text-gray-500">
                Attachments come with their email. Calendar invites and their replies stay out while Meetings is off.
              </p>
            </PartCard>

            <PartCard title="Exclusions" optional reason={edit.exclusionReason} fragment={focusedFragments.exclusions}>
              <Toggle id="purview-x-auto" label="Auto-replies" hint={'subject “Automatic reply”'}
                checked={standard.autoReplies} onChange={(autoReplies) => setStandard((s) => ({ ...s, autoReplies }))} />
              <Toggle id="purview-x-ooo" label="Out of office" hint={'subject “Out of office”'}
                checked={standard.outOfOffice} onChange={(outOfOffice) => setStandard((s) => ({ ...s, outOfOffice }))} />
              <Toggle id="purview-x-news" label="Newsletters" hint={'subject contains “newsletter”'}
                checked={standard.newsletters} onChange={(newsletters) => setStandard((s) => ({ ...s, newsletters }))} />
              <Field id="purview-x-terms" label="Other words to exclude" value={edit.exclusionTerms}
                onChange={(exclusionTerms) => update({ exclusionTerms })} />
              <p className="text-xs text-gray-500">
                The tick boxes use subject:, and subject: matches any subject that contains the words. Other words
                leave out every item that contains them. Leave an exclusion off if it could hide a real record.
              </p>
            </PartCard>
          </div>

          {/* Query, sources and the Purview steps */}
          <div className="min-w-0 space-y-4">
            <div className="space-y-2.5 rounded-lg border border-gray-900 p-3.5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">KeyQL</span>
                <div role="group" aria-label="Query variant" className="inline-flex rounded border border-gray-200 p-0.5">
                  {(['focused', 'narrower'] as const).map((v) => (
                    <button key={v} type="button" aria-pressed={variant === v}
                      onClick={() => { setVariant(v); setCopied(false); }}
                      className={cn('h-7 rounded-sm px-2.5 text-xs font-medium',
                        variant === v ? 'bg-primary text-white' : 'text-gray-600 hover:bg-gray-100')}>
                      {v === 'focused' ? 'Focused' : 'Narrower'}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-xs text-gray-600">
                {variant === 'focused'
                  ? 'The keywords, in email between the people or in documents that mention the names, in the date range.'
                  : 'Use it when the focused query returns too much.'}
              </p>
              {variant === 'narrower' && (
                <div className="space-y-2 rounded-md bg-gray-100 p-2.5">
                  <Toggle id="purview-n-subject" label="Keywords in the subject line" checked={edit.keywordsInSubject}
                    onChange={(keywordsInSubject) => update({ keywordsInSubject })} />
                  <Toggle id="purview-n-email" label="Email only" checked={edit.emailOnly}
                    onChange={(emailOnly) => update({ emailOnly })} />
                  <Field id="purview-n-require" label="Also require one of" value={edit.requireTerms}
                    onChange={(requireTerms) => update({ requireTerms })} />
                  {edit.narrowerReason && <Why text={edit.narrowerReason} />}
                </div>
              )}
              <label htmlFor="purview-keyql" className="sr-only">KeyQL query</label>
              <textarea id="purview-keyql" ref={keyqlRef} readOnly rows={12} value={built.keyql}
                className="w-full resize-y whitespace-pre-wrap rounded border border-gray-200 bg-gray-100 px-3 py-2.5 font-mono text-xs leading-relaxed text-gray-900 outline-none" />
              <QueryCounts chars={built.chars} operators={built.operators} participants={built.participants} />
              {built.warnings.length > 0 && (
                <ul className="space-y-1" aria-label="Query warnings">
                  {built.warnings.map((w) => (
                    <li key={w.message} className={cn('flex items-start gap-1.5 text-xs',
                      w.level === 'error' ? 'text-rose-600' : 'text-amber-800')}>
                      <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" /> {w.message}
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex justify-end">
                <Button onClick={copy} disabled={!built.keyql} className="min-w-[132px]">
                  {copied ? <Check className="h-4 w-4 text-green-300" /> : <Copy className="h-4 w-4" />}
                  {copied ? 'Copied' : 'Copy query'}
                </Button>
              </div>
              <p role="status" aria-live="polite" className="min-h-4 text-xs text-green-800">
                {copied ? 'Copied. Paste it in Purview under Add conditions → KeyQL.' : ''}
              </p>
            </div>

            <div className="space-y-2 rounded-lg border border-gray-200 p-3.5">
              <Label htmlFor="purview-sources" className="text-[13px] font-semibold text-gray-800">
                {canRunInPurview ? 'Data sources' : 'Data sources to add in Purview'}
              </Label>
              <p className="text-xs text-gray-500">
                {canRunInPurview
                  ? 'The mailboxes and sites to search. Run in Purview adds them to the search; in Purview, add them under Add sources. They don’t go in the KeyQL.'
                  : <>Select these in Purview → Add sources, on the search&apos;s Query tab. They don&apos;t go in the KeyQL.</>}
              </p>
              <Textarea id="purview-sources" rows={3} value={sources} onChange={(e) => setSources(e.target.value)}
                placeholder={'One per line: a mailbox (name@agency.gov) or a SharePoint site URL'} />
              {sourceLines.length > 0 && (
                <ul className="space-y-1.5">
                  {sourceLines.map((line) => (
                    <li key={line} className="break-all rounded bg-gray-100 px-2.5 py-1.5 text-xs text-gray-700">{line}</li>
                  ))}
                </ul>
              )}
            </div>

            <TooBroad near={nearExample(parts)} onNarrower={() => setVariant('narrower')} narrower={variant === 'narrower'} />

            {canRunInPurview ? (
              <>
                <PurviewRunPanel caseId={caseId} query={built.keyql} listedSources={sourceLines}
                  queryBlocked={built.warnings.some((w) => w.level === 'error')}
                  initialCase={pickedCase} context={context} onStarted={onRunStarted} />
                <details className="group rounded-lg border border-gray-200">
                  <summary className="cursor-pointer select-none px-3.5 py-2.5 text-[13px] font-medium text-gray-700">
                    Or run it yourself in Purview
                  </summary>
                  <div className="space-y-4 px-3.5 pb-3.5">
                    <PurviewSteps caseName={purviewCaseName} url={purviewUrl} />
                    <AfterExport context={context} onBack={onBack} />
                  </div>
                </details>
              </>
            ) : (
              <>
                <PurviewSteps caseName={purviewCaseName} url={purviewUrl} />
                <AfterExport context={context} onBack={onBack} />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-gray-300 px-6 py-8 text-center text-gray-700">
      <Filter className="h-7 w-7 text-gray-500" aria-hidden="true" />
      <p className="text-sm font-medium text-gray-800">No query yet</p>
      <p className="max-w-[460px] text-[13px] leading-relaxed">
        Generate a query to see its parts: keywords, people, dates, item types and exclusions. Each part says which
        words of the request it comes from, and you can edit it before copying.
      </p>
    </div>
  );
}

function LoadingState({ line }: { line: string }) {
  return (
    <div role="status" aria-live="polite" className="grid grid-cols-1 gap-6 md:grid-cols-2">
      <div className="space-y-3">
        <p className="flex items-center gap-2 text-[13px] text-gray-700">
          <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" /> {line}
        </p>
        {[40, 35, 30].map((w) => (
          <div key={w} className="space-y-2.5 rounded-lg border border-gray-200 p-3.5 animate-pulse motion-reduce:animate-none">
            <div className="h-3 rounded-sm bg-gray-200" style={{ width: `${w}%` }} />
            <div className="h-8 rounded bg-gray-100" />
          </div>
        ))}
      </div>
      <div className="h-[260px] rounded-lg border border-gray-200 p-3.5 animate-pulse motion-reduce:animate-none">
        <div className="h-3 w-1/4 rounded-sm bg-gray-200" />
      </div>
    </div>
  );
}

function Why({ text }: { text: string }) {
  return (
    <p className="text-xs leading-snug text-gray-700"><span className="font-semibold">Why:</span> {text}</p>
  );
}

function PartCard({ title, htmlFor, optional, reason, fragment, action, children }: {
  title: string;
  htmlFor?: string;
  optional?: boolean;
  reason?: string;
  fragment?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  const heading = (
    <>
      {title}
      {optional && <span className="font-normal text-gray-500"> (optional)</span>}
    </>
  );
  return (
    <section className="space-y-2 rounded-lg border border-gray-200 p-3.5" aria-label={title}>
      <div className="flex items-center justify-between gap-2">
        {htmlFor
          ? <label htmlFor={htmlFor} className="text-[13px] font-semibold text-gray-800">{heading}</label>
          : <span className="text-[13px] font-semibold text-gray-800">{heading}</span>}
        {action}
      </div>
      {children}
      {reason && <Why text={reason} />}
      <code className="block break-words rounded-sm bg-gray-100 px-1.5 py-1 font-mono text-[11.5px] text-gray-700">
        {fragment || '(nothing)'}
      </code>
    </section>
  );
}

function Field({ id, label, value, onChange, placeholder, type = 'text' }: {
  id: string; label: string; value: string; onChange: (value: string) => void; placeholder?: string; type?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id} className="text-xs">{label}</Label>
      <Input id={id} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

function Toggle({ id, label, hint, checked, onChange }: {
  id: string; label: string; hint?: string; checked: boolean; onChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-start gap-2">
      <Checkbox id={id} className="mt-0.5" checked={checked} onCheckedChange={(v) => onChange(v === true)} />
      <label htmlFor={id} className="cursor-pointer text-[13px] text-gray-800">
        {label}
        {hint && <span className="text-gray-500"> · {hint}</span>}
      </label>
    </div>
  );
}

export function QueryCounts({ chars, operators, participants }: { chars: number; operators: number; participants: number }) {
  const over = (n: number, limit: number) => n > limit;
  const near = (n: number, limit: number) => n >= limit * PURVIEW_LIMITS.nearShare;
  const tone = (n: number, limit: number) =>
    over(n, limit) ? 'font-semibold text-rose-600' : near(n, limit) ? 'font-semibold text-amber-800' : 'text-gray-500';
  return (
    <p className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs tabular-nums">
      <span className={tone(chars, PURVIEW_LIMITS.chars)}>
        {chars.toLocaleString()} / {PURVIEW_LIMITS.chars.toLocaleString()} characters
      </span>
      <span className={tone(operators, PURVIEW_LIMITS.operators)}>
        {operators} / {PURVIEW_LIMITS.operators} operators
      </span>
      {participants > 0 && (
        <span className={participants > PURVIEW_LIMITS.participants ? 'font-semibold text-amber-800' : 'text-gray-500'}>
          {participants} / {PURVIEW_LIMITS.participants} participants
        </span>
      )}
    </p>
  );
}

function TooBroad({ near, onNarrower, narrower }: { near: string | null; onNarrower: () => void; narrower: boolean }) {
  return (
    <div className="space-y-2 rounded-lg border border-orange-200 bg-orange-50 p-3.5 text-orange-800">
      <p className="flex items-center gap-2 text-[13px] font-semibold"><Filter className="h-4 w-4" /> Too broad?</p>
      <p className="text-xs leading-relaxed">
        Purview shows the item count after Run query. If it is more than your team can review, narrow it before you
        export:
      </p>
      <ul className="list-disc space-y-0.5 pl-4 text-xs leading-relaxed">
        <li>Use the Narrower query, and adjust its options under the query.</li>
        <li>Split the date range into two searches and export each.</li>
        <li>Drop a keyword that can match other work.</li>
        {near && <li>Require the names near each other: <code className="font-mono text-[11px]">{near}</code></li>}
      </ul>
      {!narrower && (
        <Button variant="outline" size="sm" onClick={onNarrower}
          className="border-orange-400 bg-white text-orange-800 hover:bg-orange-100">
          Show the narrower query
        </Button>
      )}
    </div>
  );
}

function PurviewSteps({ caseName, url }: { caseName?: string | null; url?: string | null }) {
  return (
    <div className="space-y-2 rounded-lg border border-gray-200 p-3.5">
      <p className="text-[13px] font-semibold text-gray-800">In Purview</p>
      <ol className="list-decimal space-y-1 pl-5 text-[13px] leading-relaxed text-gray-800">
        <li>
          Open <b>eDiscovery → Cases → {caseName || 'your case'} → Searches</b>, select <b>Create a search</b>, give it a
          name and select <b>Create</b>.
        </li>
        <li>On the <b>Query</b> tab, select <b>Add sources</b>, add the data sources above, then <b>Save and close</b>.</li>
        <li>
          Select <b>Add conditions → KeyQL</b> and paste the query. Fix anything listed under <b>Potential errors</b>.
        </li>
        <li>
          Select <b>Run query</b>, choose <b>Statistics</b>, and select <b>Run query</b> again. Check the item count.
        </li>
        <li>
          Select <b>Export</b>, name it, choose <b>Export items with items report</b>, then <b>Export</b>.
        </li>
      </ol>
      <p className="text-xs text-gray-500">The KeyQL condition stands alone: don&apos;t add other conditions to the same search.</p>
      <a href={url || PURVIEW_EDISCOVERY_URL} target="_blank" rel="noreferrer"
        className="inline-flex items-center gap-1.5 text-[13px] font-medium text-gray-900 hover:underline">
        {url ? 'Open the case’s Searches in Purview' : 'Open eDiscovery in Purview'} <ExternalLink className="h-3 w-3" />
      </a>
    </div>
  );
}

function AfterExport({ context, onBack }: { context: 'wizard' | 'case'; onBack?: () => void }) {
  return (
    <div className="space-y-2 rounded-lg border border-teal-300 bg-teal-50 p-3.5 text-teal-900">
      <p className="flex items-center gap-2 text-[13px] font-semibold"><Clock className="h-4 w-4" /> After the export finishes</p>
      {context === 'wizard' ? (
        <p className="text-xs leading-relaxed">
          Come back to Data collection, select Refresh exports on the Purview case, and tick the new export. Purview
          deletes an export 14 days after it is created, so import it before then.
        </p>
      ) : (
        <p className="text-xs leading-relaxed">
          Download the export&apos;s packages in Purview and add them to this case with Upload Documents (ZIP and PST
          are supported). Purview deletes an export 14 days after it is created, so download it before then.
        </p>
      )}
      {context === 'wizard' && onBack && (
        <Button variant="outline" size="sm" onClick={onBack} className="border-teal-600 bg-white text-teal-900 hover:bg-teal-100">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to data collection
        </Button>
      )}
    </div>
  );
}
